import { Link } from 'react-router-dom'
import {
  PhoneCall,
  Search,
  ThumbsUp,
  Truck,
  Droplets,
  Flame,
  Wrench,
  ShowerHead,
  Thermometer,
  AlertTriangle,
  Star,
  CheckCircle2,
  Clock,
  BadgePoundSterling,
  Snowflake,
  Gauge,
  Bath,
  Zap,
  Wind,
  RotateCcw,
  Waves,
  Settings,
} from 'lucide-react'
import { EmergencyForm } from '../components/EmergencyForm'
import { TrustSignals } from '../components/ui/TrustSignals'
import { Navbar } from '../components/layout/Navbar'
import { HeroMap } from '../components/map/HeroMap'
import { Footer } from '../components/layout/Footer'
import { useAppConfig } from '../hooks/useAppConfig'
import { Seo } from '../components/Seo'
import { SUPPORT_PHONE_TEL, SHOW_SUPPORT_PHONE } from '../lib/contact'

// ── Other page data ────────────────────────────────────────────────────────────

const steps = [
  { icon: PhoneCall, title: 'Tell us the problem', desc: 'Your emergency and postcode. Takes under a minute.' },
  { icon: Search,   title: 'We contact local plumbers', desc: 'We reach out to qualified plumbers near you simultaneously.' },
  { icon: ThumbsUp, title: 'We find the fastest', desc: 'Best availability and price. We share the match with you.' },
  { icon: Truck,    title: 'Plumber on the way', desc: 'Pay the callout fee directly on arrival. Nothing more.' },
]

const emergencies = [
  { icon: Droplets,      label: 'Burst pipes' },
  { icon: AlertTriangle, label: 'Leaks & flooding' },
  { icon: Flame,         label: 'Boiler breakdown' },
  { icon: ShowerHead,    label: 'No hot water' },
  { icon: Thermometer,   label: 'Heating failure' },
  { icon: Wrench,        label: 'Blocked drains' },
  { icon: Snowflake,     label: 'Frozen pipes' },
  { icon: Wind,          label: 'Gas leak' },
  { icon: Gauge,         label: 'Low water pressure' },
  { icon: Bath,          label: 'Bathroom plumbing' },
  { icon: RotateCcw,     label: 'Power flushing' },
  { icon: Zap,           label: 'Immersion heater' },
  { icon: Waves,         label: 'Overflowing toilet' },
  { icon: Settings,      label: 'Stopcock issues' },
]

const stats = [
  { value: '6,000+', label: 'Plumbers on platform' },
  { value: 'Fast',   label: 'Response times' },
  { value: '24/7',   label: 'Always available' },
  { value: '4.8 ★',  label: 'Customer rating' },
]

const testimonials = [
  {
    name: 'Sarah M.',
    location: 'Manchester',
    text: 'Burst pipe at midnight. FixDelivered had a plumber at my door within 35 minutes. Genuinely lifesaving service.',
    stars: 5,
    avatar: 'https://api.dicebear.com/9.x/notionists/svg?seed=Sarah&backgroundColor=fef3c7',
  },
  {
    name: 'James T.',
    location: 'London',
    text: 'Boiler went on a Sunday. They found me a plumber and got me a quote £40 cheaper than I expected.',
    stars: 5,
    avatar: 'https://api.dicebear.com/9.x/notionists/svg?seed=James&backgroundColor=dbeafe',
  },
  {
    name: 'Priya K.',
    location: 'Birmingham',
    text: 'Really impressed - no endless phone calls, no being put on hold. Just a quick form and a plumber showed up.',
    stars: 5,
    avatar: 'https://api.dicebear.com/9.x/notionists/svg?seed=Priya&backgroundColor=fce7f3',
  },
  {
    name: 'Tom R.',
    location: 'Leeds',
    text: 'Heating packed in on Christmas Eve. Had someone booked within 20 minutes. Cannot recommend enough.',
    stars: 5,
    avatar: 'https://api.dicebear.com/9.x/notionists/svg?seed=Tom&backgroundColor=dcfce7',
  },
  {
    name: 'Claire B.',
    location: 'Bristol',
    text: 'Used it for a major leak under the kitchen sink. Professional, fast, and the booking fee was absolutely worth it.',
    stars: 5,
    avatar: 'https://api.dicebear.com/9.x/notionists/svg?seed=Claire&backgroundColor=ede9fe',
    mentionsFee: true,
  },
  {
    name: 'David H.',
    location: 'Sheffield',
    text: 'Skeptical at first but it genuinely works. Plumber arrived within the hour, fixed the problem first time.',
    stars: 5,
    avatar: 'https://api.dicebear.com/9.x/notionists/svg?seed=David&backgroundColor=e0f2fe',
  },
  {
    name: 'Aisha N.',
    location: 'London',
    text: 'No hot water with a newborn at home. FixDelivered sorted it in under an hour. Absolute lifesaver.',
    stars: 5,
    avatar: 'https://api.dicebear.com/9.x/notionists/svg?seed=Aisha&backgroundColor=fff7ed',
  },
  {
    name: 'Mark L.',
    location: 'Edinburgh',
    text: 'Tried ringing around myself first - wasted an hour. Wish I had just used FixDelivered straight away.',
    stars: 5,
    avatar: 'https://api.dicebear.com/9.x/notionists/svg?seed=Mark&backgroundColor=f0fdf4',
  },
]

// ── Logo badge with text fallback ──────────────────────────────────────────────

function LogoBadge({
  src,
  alt,
  fallback,
  width,
  height,
  className = 'h-10 w-auto object-contain',
}: {
  src: string
  alt: string
  fallback: string
  width: number
  height: number
  className?: string
}) {
  return (
    <img
      src={src}
      alt={alt}
      width={width}
      height={height}
      className={className}
      onError={e => {
        const el = e.currentTarget
        el.style.display = 'none'
        const badge = document.createElement('span')
        badge.textContent = fallback
        badge.className = 'inline-block text-xs font-bold uppercase tracking-wide text-gray-500 border border-gray-300 rounded px-2 py-1'
        el.parentNode?.appendChild(badge)
      }}
    />
  )
}

// ── Section CTA ─────────────────────────────────────────────────────────────────
// Scrolls back to the hero emergency form so every section has a conversion path.
function scrollToForm() {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

function SectionCta({ label }: { label: string }) {
  return (
    <div className="mt-10 text-center">
      <button
        type="button"
        onClick={scrollToForm}
        className="inline-flex items-center gap-2 bg-emergency-600 hover:bg-emergency-700 text-white font-bold px-8 py-4 rounded-xl text-lg transition-colors shadow-sm"
      >
        {label} <span aria-hidden="true">→</span>
      </button>
    </div>
  )
}

// ── Component ──────────────────────────────────────────────────────────────────

export function Home() {
  const { platformFeeGbp, paywallEnabled } = useAppConfig()

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="FixDelivered - Emergency Plumber Confirmed Within 1 Hour"
        description={paywallEnabled
          ? `Emergency plumber needed? FixDelivered finds and confirms a qualified local plumber near you, typically within the hour. £${platformFeeGbp} flat booking fee, fully refundable if we can't deliver. Available 24/7 across the UK.`
          : `Emergency plumber needed? FixDelivered finds and confirms a qualified local plumber near you, typically within the hour - completely free, no booking fee. Available 24/7 across the UK.`}
        path="/"
        structuredData={[
          {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'FixDelivered',
            url: 'https://fixdelivered.com',
            logo: 'https://fixdelivered.com/apple-touch-icon.png',
            image: 'https://fixdelivered.com/apple-touch-icon.png',
            description: 'FixDelivered finds and confirms a qualified local emergency plumber near you, usually within the hour.',
            areaServed: { '@type': 'Country', name: 'United Kingdom' },
            // Issue #49 - a real, working number search engines/knowledge
            // panels can surface. Gated the same as everywhere else so this
            // never advertises a number that isn't actually live.
            ...(SHOW_SUPPORT_PHONE && { telephone: SUPPORT_PHONE_TEL }),
          },
          {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'FixDelivered',
            url: 'https://fixdelivered.com',
          },
        ]}
      />
      <Navbar />

      <main>
      {/* ── Hero ─────────────────────────────────────────────────────────────── */}
      <section className="hero-bg relative overflow-hidden">

        <HeroMap />

        <div className="relative z-10 max-w-3xl mx-auto px-4 py-8 sm:py-16 lg:py-24">

          {/* Badge */}
          <div className="flex justify-center mb-6 animate-fade-up">
            <span className="inline-flex items-center gap-2 bg-white border border-gray-200 text-gray-600 text-xs font-semibold px-4 py-1.5 rounded-full shadow-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-emergency-500 pulse-dot" />
              Emergency service · Available 24/7
            </span>
          </div>

          {/* Headline */}
          <div className="text-center mb-4 animate-fade-up delay-100">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight text-gray-900">
              Plumbing emergency?
            </h1>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight mt-1 text-emergency-500">
              We'll fix it - fast.
            </h2>
          </div>

          {/* Subheading */}
          <p className="text-center text-sm sm:text-lg text-gray-500 max-w-xl mx-auto mb-8 sm:mb-10 animate-fade-up delay-200">
            We contact every qualified plumber near you - all at once - so you're not stuck ringing
            around during an emergency.{paywallEnabled ? ' Can\'t find you one? Full refund, automatically.' : ' It\'s completely free right now, no booking fee.'}
          </p>

          {/* Form card */}
          <div id="emergency-form" className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-200 shadow-xl animate-fade-up delay-400">
            <EmergencyForm />
          </div>

          {/* Trust signals */}
          <div className="mt-6 animate-fade-up delay-500">
            <TrustSignals />
          </div>
        </div>
      </section>

      {/* ── Stats bar ──────────────────────────────────────────────────────── */}
      <section className="border-t border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 text-center">
            {stats.map(({ value, label }, i) => (
              <div key={label} className={`px-4 py-2 ${i % 2 === 0 ? 'border-r border-gray-100' : ''} ${i < 2 ? 'border-b sm:border-b-0 border-gray-100' : ''} sm:border-r sm:last:border-r-0`}>
                <p className="text-2xl sm:text-3xl font-bold text-gray-900">{value}</p>
                <p className="text-xs text-gray-400 mt-1 font-medium">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Plumber qualifications ─────────────────────────────────────────── */}
      <section className="bg-white py-10 sm:py-16">
        <div className="max-w-4xl mx-auto px-4">
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 text-center mb-2">Qualified &amp; reviewed</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-3">Credentials you can check</h2>
          <p className="text-center text-sm text-gray-500 mb-12 max-w-lg mx-auto">
            We build every profile from public trade registers and review data, cross-reference qualifications, and only
            list plumbers rated 4★ and above. See each plumber's Gas Safe and WaterSafe credentials on their profile
            before you book.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-12">
            {/* Gas Safe */}
            <div className="flex flex-col gap-3 bg-white border border-gray-200 rounded-2xl p-6">
              <div className="h-10 flex items-center mb-3">
                <LogoBadge src="/logos/gasSafe.svg" alt="Gas Safe Register" fallback="Gas Safe" width={120} height={135} />
              </div>
              <h3 className="font-semibold text-gray-900 text-sm">Gas Safe registered</h3>
              <p className="text-sm text-gray-500 leading-relaxed">A Gas Safe registered engineer is the legal requirement for gas work in the UK. View a plumber's Gas Safe qualifications and make sure you're getting the right engineer for the job.</p>
            </div>

            {/* WaterSafe */}
            <div className="flex flex-col gap-3 bg-white border border-gray-200 rounded-2xl p-6">
              <div className="h-10 flex items-center mb-3">
                <LogoBadge
                  src="/logos/watersafe.svg"
                  alt="WaterSafe"
                  fallback="WaterSafe"
                  width={1920}
                  height={377}
                  className="h-7 w-auto object-contain"
                />
              </div>
              <h3 className="font-semibold text-gray-900 text-sm">WaterSafe approved</h3>
              <p className="text-sm text-gray-500 leading-relaxed">Many of our plumbers hold WaterSafe approval, ensuring all work meets Water Regulations and industry standards.</p>
            </div>

          </div>

        </div>
      <SectionCta label="Find a qualified plumber" />
      </section>

      {/* ── What we handle ─────────────────────────────────────────────────── */}
      <section className="bg-gray-50 py-10 sm:py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">Emergencies we cover</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-8">Whatever the plumbing crisis</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {emergencies.map(({ icon: Icon, label }) => (
              <span
                key={label}
                className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2 text-sm font-medium text-gray-700 shadow-sm"
              >
                <Icon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                {label}
              </span>
            ))}
          </div>
        </div>
      <SectionCta label="Get help with your emergency" />
      </section>

      {/* ── Why FixDelivered ───────────────────────────────────────────────── */}
      <section className="bg-white py-10 sm:py-16">
        <div className="max-w-5xl mx-auto px-4">
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">Why us</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-10">More than just a booking service</h2>

          {/* Bento grid: wide + narrow alternating */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

            {/* Wide card — rates */}
            <div className="sm:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex items-center gap-5">
              <div className="w-14 h-14 rounded-2xl bg-gold-50 flex items-center justify-center flex-shrink-0">
                <svg viewBox="0 0 64 64" fill="none" className="w-9 h-9" aria-hidden="true">
                  <circle cx="32" cy="32" r="20" fill="#fef3c7" stroke="#e8a820" strokeWidth="2.5"/>
                  <text x="32" y="38" textAnchor="middle" fontSize="22" fontWeight="700" fill="#c48a18" fontFamily="sans-serif">£</text>
                  <path d="M18 14 L14 10 M46 14 L50 10 M54 32 L58 32 M10 32 L6 32" stroke="#f0bc40" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="20" cy="20" r="2" fill="#f0bc40"/>
                  <circle cx="44" cy="20" r="1.5" fill="#f0bc40"/>
                  <circle cx="48" cy="40" r="2" fill="#fde68a"/>
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1.5 text-sm">Always the sharpest price</h3>
                <p className="text-sm text-gray-500 leading-relaxed">Multiple plumbers compete for your job in real time. More competition, better price.</p>
              </div>
            </div>

            {/* Narrow card — advice */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex flex-col gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gold-50 flex items-center justify-center flex-shrink-0">
                <svg viewBox="0 0 64 64" fill="none" className="w-9 h-9" aria-hidden="true">
                  <line x1="32" y1="12" x2="32" y2="52" stroke="#e8a820" strokeWidth="2.5" strokeLinecap="round"/>
                  <line x1="20" y1="20" x2="44" y2="20" stroke="#e8a820" strokeWidth="2.5" strokeLinecap="round"/>
                  <line x1="20" y1="20" x2="14" y2="32" stroke="#f0bc40" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="44" y1="20" x2="50" y2="32" stroke="#f0bc40" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M10 32 Q14 38 18 32" stroke="#e8a820" strokeWidth="2" fill="#fef3c7" strokeLinecap="round"/>
                  <path d="M46 32 Q50 38 54 32" stroke="#e8a820" strokeWidth="2" fill="#fef3c7" strokeLinecap="round"/>
                  <line x1="24" y1="52" x2="40" y2="52" stroke="#e8a820" strokeWidth="2.5" strokeLinecap="round"/>
                  <circle cx="32" cy="12" r="3" fill="#e8a820"/>
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1.5 text-sm">Honest advice, start to finish</h3>
                <p className="text-sm text-gray-500 leading-relaxed">No commission, no upselling. We're paid the same regardless of who we send.</p>
              </div>
            </div>

            {/* Full-width card — we handle everything */}
            <div className="sm:col-span-3 bg-white rounded-2xl border-2 border-gold-400 shadow-sm p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-6">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 text-sm mb-1.5">We handle it all. You just answer the door.</h3>
                <p className="text-sm text-gray-500 leading-relaxed max-w-xl">We contact plumbers, collect quotes and confirm the booking on your behalf. You'll only ever pay for work you've agreed to.</p>
              </div>
              <div className="flex flex-col gap-2.5 flex-shrink-0 sm:min-w-[190px]">
                {[
                  'We make all the calls',
                  'You approve the quote',
                  'Pay only for agreed work',
                ].map(point => (
                  <div key={point} className="flex items-center gap-2.5">
                    <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                      <svg viewBox="0 0 12 12" fill="none" className="w-2.5 h-2.5"><path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </div>
                    <span className="text-sm text-gray-600 font-medium">{point}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Narrow card — independent */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex flex-col gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gold-50 flex items-center justify-center flex-shrink-0">
                <svg viewBox="0 0 64 64" fill="none" className="w-9 h-9" aria-hidden="true">
                  <path d="M10 30 L32 12 L54 30" stroke="#e8a820" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round"/>
                  <path d="M16 26 L16 52 L48 52 L48 26" stroke="#e8a820" strokeWidth="2.5" strokeLinejoin="round" fill="#fef3c7"/>
                  <rect x="26" y="38" width="12" height="14" rx="2" stroke="#e8a820" strokeWidth="2" fill="white"/>
                  <path d="M32 23 C32 23 28 19.5 28 22 C28 24 30 25.5 32 27 C34 25.5 36 24 36 22 C36 19.5 32 23 32 23Z" fill="#f0bc40"/>
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1.5 text-sm">Back your local community</h3>
                <p className="text-sm text-gray-500 leading-relaxed">Local independents only. Your money stays in the community.</p>
              </div>
            </div>

            {/* Wide card — protection */}
            <div className="sm:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex items-center gap-5">
              <div className="w-14 h-14 rounded-2xl bg-gold-50 flex items-center justify-center flex-shrink-0">
                <svg viewBox="0 0 64 64" fill="none" className="w-9 h-9" aria-hidden="true">
                  <path d="M32 10 L52 18 L52 34 C52 44 43 51 32 54 C21 51 12 44 12 34 L12 18 Z" fill="#fef3c7" stroke="#e8a820" strokeWidth="2.5" strokeLinejoin="round"/>
                  <path d="M22 32 L29 39 L42 25" stroke="#c48a18" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1.5 text-sm">Book with total confidence</h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  {paywallEnabled
                    ? "Can't find a plumber? Full refund, automatically. No questions asked."
                    : "It's completely free right now, no booking fee - and we don't stop trying until we find you a qualified plumber."}
                </p>
              </div>
            </div>

          </div>
        </div>
      <SectionCta label="Start your callout" />
      </section>

      {/* ── Pricing transparency ─────────────────────────────────────────────── */}
      {paywallEnabled ? (
      <section className="bg-gray-50 py-10 sm:py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-10 sm:gap-16">
            <div className="flex-shrink-0 text-center sm:text-left">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">Our fee</p>
              <p className="text-7xl sm:text-8xl font-black text-gray-900 leading-none">£{platformFeeGbp}</p>
              <p className="text-gray-400 text-sm mt-2 font-medium">flat booking fee</p>
            </div>
            <div className="hidden sm:block w-px self-stretch bg-gray-200" />
            <div className="flex flex-col gap-5 flex-1">
              {[
                { icon: BadgePoundSterling, title: 'Charged upfront, covers everything', desc: 'Taken before we start the search. If we find you a plumber or not, that covers our service.' },
                { icon: CheckCircle2,       title: 'Fully refundable if we fail', desc: 'No plumber found? Refunded automatically - no hold times, no forms to fill in.' },
                { icon: Clock,             title: 'Pay the plumber directly on arrival', desc: 'Callout fees and any further work are settled with your plumber face to face.' },
              ].map(({ icon: Icon, title, desc }) => (
                <div key={title} className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon className="h-4 w-4 text-gray-500" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 mb-0.5">{title}</p>
                    <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      <SectionCta label="Get help now" />
      </section>
      ) : (
      <section className="bg-gray-50 py-10 sm:py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-10 sm:gap-16">
            <div className="flex-shrink-0 text-center sm:text-left">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">Our fee</p>
              <p className="text-7xl sm:text-8xl font-black text-emerald-600 leading-none">Free</p>
              <p className="text-gray-400 text-sm mt-2 font-medium">totally free right now</p>
            </div>
            <div className="hidden sm:block w-px self-stretch bg-gray-200" />
            <div className="flex flex-col gap-5 flex-1">
              {[
                { icon: BadgePoundSterling, title: 'No booking fee, no catch', desc: 'Finding, comparing and confirming a plumber costs nothing right now - not upfront, not later.' },
                { icon: CheckCircle2,       title: 'Nothing to refund, because nothing is charged', desc: "No plumber found? No problem - there was never a fee taken in the first place." },
                { icon: Clock,             title: 'Pay the plumber directly on arrival', desc: 'The only cost is the callout fee you agree with your plumber - settled with them face to face.' },
              ].map(({ icon: Icon, title, desc }) => (
                <div key={title} className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon className="h-4 w-4 text-gray-500" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 mb-0.5">{title}</p>
                    <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      <SectionCta label="Get help now" />
      </section>
      )}

      {/* ── How it works ───────────────────────────────────────────────────── */}
      <section className="bg-white py-10 sm:py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">The process</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-10">How FixDelivered works</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8">
            {steps.map(({ icon: Icon, title, desc }, i) => (
              <div key={title} className="flex flex-col items-center text-center gap-3">
                <div className="relative">
                  <div className="w-14 h-14 rounded-2xl bg-gray-50 border border-gray-200 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-gray-600" />
                  </div>
                  <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-gold-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">
                    {i + 1}
                  </span>
                </div>
                <p className="font-semibold text-gray-900 text-sm">{title}</p>
                <p className="text-xs text-gray-400 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/how-it-works" className="text-gray-500 text-sm font-medium hover:text-gray-800 transition-colors">
              Learn more about how it works →
            </Link>
          </div>
        </div>
      <SectionCta label="Get emergency help" />
      </section>

      {/* ── Testimonials ───────────────────────────────────────────────────── */}
      <section className="bg-gray-50 py-10 sm:py-16 overflow-hidden">
        <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 text-center mb-2">Real customers</p>
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-10">What people are saying</h2>

        <div className="relative">
          {/* Fade left */}
          <div className="absolute left-0 top-0 bottom-0 w-8 sm:w-24 z-10 pointer-events-none"
            style={{ background: 'linear-gradient(to right, #f9fafb, transparent)' }} />
          {/* Fade right */}
          <div className="absolute right-0 top-0 bottom-0 w-8 sm:w-24 z-10 pointer-events-none"
            style={{ background: 'linear-gradient(to left, #f9fafb, transparent)' }} />

          <div className="testimonial-track">
            {(() => {
              const shown = paywallEnabled ? testimonials : testimonials.filter(t => !t.mentionsFee)
              return [...shown, ...shown]
            })().map(({ name, location, text, stars, avatar }, i) => (
              <div key={i} className="flex-shrink-0 w-80 bg-white rounded-xl border border-gray-100 shadow-sm p-6 flex flex-col gap-3">
                <div className="flex gap-0.5">
                  {Array.from({ length: stars }).map((_, j) => (
                    <Star key={j} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-sm text-gray-600 leading-relaxed flex-1">"{text}"</p>
                <div className="flex items-center gap-3">
                  <img
                    src={avatar}
                    alt={name}
                    className="w-9 h-9 rounded-full object-cover bg-gray-100 flex-shrink-0"
                    loading="lazy"
                  />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{name}</p>
                    <p className="text-xs text-gray-400">{location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      <SectionCta label="Get help now" />
      </section>
      </main>

      <Footer />
    </div>
  )
}
